/**
 * ============================================================================
 * TRANSACTION STORE - ZUSTAND
 * ============================================================================
 * 
 * Global transaction state management
 * 
 * @typedef {Object} Transaction
 * @typedef {Object} TransactionFilters
 * @typedef {Object} TransactionPagination
 * @typedef {Object} TransactionState
 * @property {Transaction[]} transactions
 * @property {Transaction|null} currentTransaction
 * @property {Object|null} transactionStats
 * @property {Object|null} summary
 * @property {Array} timeline
 * @property {Array} statusHistory
 * @property {boolean} loading
 * @property {string|null} error
 * @property {TransactionFilters} filters
 * @property {TransactionPagination} pagination
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { validateId, validateArray, validateObject, validateTransaction, validateFilters, validatePagination } from './utils/validators';
import { addItemOptimized, updateItemInArray, removeItemFromArray, limitArraySize, deduplicateById } from './utils/performance';
import { createSelector } from './utils/selectors';

const useTransactionStore = create(
  persist(
    (set, get) => ({
      // State
      transactions: [],
      currentTransaction: null,
      transactionStats: null,
      summary: null,
      timeline: [],
      statusHistory: [],
      loading: false,
      error: null,
      filters: {
        status: null,
        type: null,
        dateRange: null,
        search: null,
      },
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0,
      },

      // Actions
      /**
       * Set transactions with validation and deduplication
       * @param {Transaction[]} transactions - Array of transactions
       */
      setTransactions: (transactions) => {
        validateArray(transactions, 'Transactions');
        const validated = transactions.map((t) => {
          validateTransaction(t);
          return t;
        });
        const deduplicated = deduplicateById(validated);
        const limited = limitArraySize(deduplicated);
        set({ transactions: limited });
      },

      /**
       * Add transaction with validation, deduplication, and size limit
       * @param {Transaction} transaction - Transaction to add
       */
      addTransaction: (transaction) => {
        validateTransaction(transaction);
        set((state) => ({
          transactions: addItemOptimized(state.transactions, transaction, {
            idKey: 'id',
            maxSize: 1000,
            prepend: true,
          }),
        }));
      },

      /**
       * Update transaction with validation
       * @param {string} transactionId - Transaction ID
       * @param {Object} updates - Updates to apply
       */
      updateTransaction: (transactionId, updates) => {
        validateId(transactionId, 'Transaction ID');
        validateObject(updates, 'Updates');
        set((state) => ({
          transactions: updateItemInArray(state.transactions, transactionId, updates),
          currentTransaction:
            state.currentTransaction?.id === transactionId
              ? { ...state.currentTransaction, ...updates }
              : state.currentTransaction,
        }));
      },

      /**
       * Remove transaction with validation
       * @param {string} transactionId - Transaction ID
       */
      removeTransaction: (transactionId) => {
        validateId(transactionId, 'Transaction ID');
        set((state) => ({
          transactions: removeItemFromArray(state.transactions, transactionId),
          currentTransaction:
            state.currentTransaction?.id === transactionId
              ? null
              : state.currentTransaction,
        }));
      },

      setCurrentTransaction: (transaction) => {
        set({ currentTransaction: transaction });
      },

      setTransactionStats: (stats) => {
        set({ transactionStats: stats });
      },

      setSummary: (summary) => {
        set({ summary });
      },

      setTimeline: (timeline) => {
        set({ timeline });
      },

      setStatusHistory: (statusHistory) => {
        set({ statusHistory });
      },

      /**
       * Set filters with validation
       * @param {TransactionFilters} filters - Filter options
       */
      setFilters: (filters) => {
        validateFilters(filters);
        set((state) => ({
          filters: { ...state.filters, ...filters },
          pagination: { ...state.pagination, page: 1 }, // Reset to first page
        }));
      },

      clearFilters: () => {
        set({
          filters: {
            status: null,
            type: null,
            dateRange: null,
            search: null,
          },
          pagination: { ...get().pagination, page: 1 },
        });
      },

      /**
       * Set pagination with validation
       * @param {TransactionPagination} pagination - Pagination options
       */
      setPagination: (pagination) => {
        validatePagination(pagination);
        set((state) => ({
          pagination: { ...state.pagination, ...pagination },
        }));
      },

      setLoading: (loading) => {
        set({ loading });
      },

      setError: (error) => {
        set({ error });
      },

      clearError: () => {
        set({ error: null });
      },

      reset: () => {
        set({
          transactions: [],
          currentTransaction: null,
          transactionStats: null,
          summary: null,
          timeline: [],
          statusHistory: [],
          loading: false,
          error: null,
          filters: {
            status: null,
            type: null,
            dateRange: null,
            search: null,
          },
          pagination: {
            page: 1,
            pageSize: 10,
            total: 0,
          },
        });
      },
    }),
    {
      name: 'transaction-storage',
      partialize: (state) => ({
        filters: state.filters,
        pagination: state.pagination,
        // Don't persist transactions, stats, etc. - fetch fresh on load
      }),
    }
  )
);

// Memoized Selectors
export const selectFilteredTransactions = createSelector(
  (state) => {
    const { transactions, filters } = state;
    if (!filters || Object.values(filters).every((v) => v === null || v === undefined)) {
      return transactions;
    }

    return transactions.filter((transaction) => {
      if (filters.status && transaction.status !== filters.status) return false;
      if (filters.type && transaction.type !== filters.type) return false;
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch =
          transaction.title?.toLowerCase().includes(searchLower) ||
          transaction.description?.toLowerCase().includes(searchLower) ||
          transaction.id?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }
      // Date range filtering would go here
      return true;
    });
  }
);

export const selectTransactionById = createSelector(
  (state, transactionId) => {
    return state.transactions.find((t) => t.id === transactionId) || null;
  }
);

export const selectTransactionStats = createSelector(
  (state) => {
    const { transactions } = state;
    return {
      total: transactions.length,
      byStatus: transactions.reduce((acc, t) => {
        acc[t.status] = (acc[t.status] || 0) + 1;
        return acc;
      }, {}),
      totalAmount: transactions.reduce((sum, t) => sum + (t.amount || 0), 0),
    };
  }
);

export default useTransactionStore;
